import ExcelJS from "exceljs";
import { saveAs } from "file-saver";

// Builds an export workbook with a Summary sheet followed by one sheet per
// group (customer, supplier, ...). Each Summary name links to its sheet.

const NO_NAME = "No Name";
const SHEET_NAME_MAX = 31; // Excel limit

// Excel's built-in hyperlink look, so linked names read as clickable
const LINK_FONT = { color: { argb: "FF0563C1" }, underline: true };

// Returns a valid Excel sheet name for `name` that isn't in `usedNames`
// (case-insensitive, as Excel compares them), and records it there. "!" is
// also replaced, since the Summary links use "'Sheet'!A1".
export function uniqueSheetName(name, usedNames, fallback = NO_NAME) {
  const base =
    name
      .replace(/[\\/?*[\]:!]/g, "-")
      .replace(/^'+|'+$/g, "")
      .trim() || fallback;

  let candidate = base.slice(0, SHEET_NAME_MAX).trim();
  for (let n = 2; usedNames.has(candidate.toLowerCase()); n++) {
    const suffix = ` (${n})`;
    candidate = base.slice(0, SHEET_NAME_MAX - suffix.length).trim() + suffix;
  }
  usedNames.add(candidate.toLowerCase());
  return candidate;
}

// Record count and total per currency, e.g. [{ currency, count, total }]
function totalsByCurrency(records, getCurrency, getAmount) {
  const totals = new Map();
  records.forEach((record) => {
    const currency = getCurrency(record);
    const entry = totals.get(currency) || { currency, count: 0, total: 0 };
    entry.count += 1;
    entry.total += Number(getAmount(record)) || 0;
    totals.set(currency, entry);
  });
  return [...totals.values()].map((entry) => ({
    ...entry,
    total: Math.round(entry.total * 100) / 100,
  }));
}

// Report header shown above the content of every sheet
function getHeaderRows(meta, groupLabel, groupName) {
  const rows = [
    [`${meta.title} - ${meta.status}`],
    ["Date Range", meta.dateRange || ""],
    ["Status", meta.statusLabel || meta.status],
  ];
  if (groupName !== undefined) rows.push([groupLabel, groupName]);
  rows.push([]);
  return rows;
}

function linkFormula(sheetName, text) {
  const target = `#'${sheetName.replace(/'/g, "''")}'!A1`;
  return `HYPERLINK("${target.replace(/"/g, '""')}","${text.replace(/"/g, '""')}")`;
}

function addSummarySheet(workbook, sheetName, config, meta, groups, records) {
  const { groupLabel, countLabel, getCurrency, getAmount } = config;
  const sheet = workbook.addWorksheet(sheetName);
  sheet.addRows(getHeaderRows(meta));
  sheet.addRow([groupLabel, "Currency", countLabel, "Total Amount"]);

  groups.forEach((group) => {
    totalsByCurrency(group.records, getCurrency, getAmount).forEach((entry) => {
      const row = sheet.addRow([
        group.name,
        entry.currency,
        entry.count,
        entry.total,
      ]);
      // A HYPERLINK formula is used because exceljs writes cell hyperlinks
      // as external URLs, which Excel can't open.
      const cell = row.getCell(1);
      cell.value = {
        formula: linkFormula(group.sheetName, group.name),
        result: group.name,
      };
      cell.font = LINK_FONT;
    });
  });

  sheet.addRow([]);
  totalsByCurrency(records, getCurrency, getAmount).forEach((entry) => {
    sheet.addRow(["Grand Total", entry.currency, entry.count, entry.total]);
  });

  sheet.columns = [{ width: 45 }, { width: 10 }, { width: 15 }, { width: 18 }];
  // Show totals as amounts, e.g. 12,345.60
  sheet.getColumn(4).numFmt = "#,##0.00";
}

// Groups records by config.getGroup(record) -> { key, name }, sorted A-Z
function groupRecords(records, getGroup, fallbackName) {
  const groups = new Map();
  records.forEach((record) => {
    const { key, name } = getGroup(record);
    const groupName = (name || "").trim() || fallbackName;
    const groupKey = key || groupName;
    if (!groups.has(groupKey)) {
      groups.set(groupKey, { name: groupName, records: [] });
    }
    groups.get(groupKey).records.push(record);
  });
  return [...groups.values()].sort((a, b) => a.name.localeCompare(b.name));
}

// Writes and downloads the workbook.
//   records - the rows of one export (one status)
//   meta    - from buildExportMeta
//   config  - {
//     groupLabel:     "Customer" | "Supplier" ...
//     countLabel:     "No. of Invoices" ...
//     getGroup:       (record) => ({ key, name })
//     getCurrency:    (record) => "PHP"
//     getAmount:      (record) => number
//     buildBlockRows: (record) => rows (array of arrays) for one record
//     tableColumns:   optional header row added once above the records, for
//                     lists where buildBlockRows returns a single table row
//   }
export async function exportGroupedExcel(records, meta, config) {
  const fallbackName = `No ${config.groupLabel}`;
  const workbook = new ExcelJS.Workbook();
  const usedNames = new Set();

  const summaryName = uniqueSheetName("Summary", usedNames);
  const groups = groupRecords(records, config.getGroup, fallbackName).map(
    (group) => ({
      ...group,
      sheetName: uniqueSheetName(group.name, usedNames, fallbackName),
    }),
  );

  addSummarySheet(workbook, summaryName, config, meta, groups, records);
  groups.forEach((group) => {
    const sheet = workbook.addWorksheet(group.sheetName);
    sheet.addRows(getHeaderRows(meta, config.groupLabel, group.name));
    if (config.tableColumns) {
      sheet.addRow(config.tableColumns).font = { bold: true };
      sheet.columns = config.tableColumns.map(() => ({ width: 20 }));
    }
    group.records.forEach((record) => {
      sheet.addRows(config.buildBlockRows(record));
    });
  });

  const buffer = await workbook.xlsx.writeBuffer();
  saveAs(new Blob([buffer]), `${meta.fileName}.xlsx`);
}
